import { useLocalization } from "@fluent/react";
import { Button, useBreakpoints } from "@narmi/design_system";
import { Card } from "@lending/components/Card/Card";
import { CreditCardImage } from "@lending/components/CreditCardImage/CreditCardImage";
import { CreditCardProduct } from "@lending/types";
import { formatCentsAsDollarsAndCents } from "@lending/utils/formatters";
import { getCreditCardNetwork } from "@lending/utils/getCreditCardNetwork";
import styles from "./OfferSelect.module.css";

interface BaseOfferCardProps {
  monthly_payment?: number;
  emphasize?: boolean;
  chipLabel?: string;
  bulletPoints?: string[];
  infoLinkText?: string;
  infoLinkUrl?: string;
  onSelect?: () => void;
  isPending?: boolean;
  isDisabled?: boolean;
  isFullWidthCta?: boolean;
}

interface CreditCardOfferCardProps extends BaseOfferCardProps {
  kind: "credit_card";
  cardName?: string;
  cardNetwork: CreditCardProduct["card_network"];
}

interface LoanOfferCardProps extends BaseOfferCardProps {
  kind: "loan";
}

type OfferCardProps = CreditCardOfferCardProps | LoanOfferCardProps;

function OfferSelect(props: OfferCardProps) {
  const { l10n } = useLocalization();
  const { s, m } = useBreakpoints();

  return (
    <div className={styles.offerCard}>
      {props.chipLabel && <div className={styles.chip}>{props.chipLabel}</div>}
      <Card className={styles.cardContent} emphasize={props.emphasize}>
        {props.kind === "loan" && (
          <div className={styles.loanHeader}>
            <div className="fontFamily--heading fontSize--heading1 fontColor--theme--primary margin--bottom--xs">
              {props.monthly_payment ? formatCentsAsDollarsAndCents(props.monthly_payment) : ""}
            </div>
            <div className="fontSize--xs fontColor--secondary">
              {l10n.getString("offer-card-monthly-payment", null, "Monthly payment")}
            </div>
          </div>
        )}
        {props.kind === "credit_card" && (
          <div className={styles.creditCardHeader}>
            {m && (
              <CreditCardImage
                width={185}
                height={116}
                network={getCreditCardNetwork(props.cardNetwork)}
                cardName={props.cardName}
              />
            )}
            <span className={styles.cardTitle + " fontColor--primary fontWeight--bold"}>
              {props.cardName}
            </span>
          </div>
        )}
        <hr className={styles.divider} />
        <ul className="list--reset fontSize--s">
          <li className="margin--bottom--s">
            <i className="narmi-icon-check fontColor--theme--primary" />
            <span className="margin--left--xxs fontWeight--semibold">
              {props.bulletPoints?.[0] ?? ""}
            </span>
          </li>
          <li className="margin--bottom--s">
            <i className="narmi-icon-check fontColor--theme--primary" />
            <span className="margin--left--xxs fontWeight--semibold">
              {props.bulletPoints?.[1] ?? ""}
            </span>
          </li>
          <li className="margin--bottom--s">
            <i className="narmi-icon-check fontColor--theme--primary" />
            <span className="margin--left--xxs fontWeight--semibold">
              {props.bulletPoints?.[2] ?? ""}
            </span>
          </li>
        </ul>
        <div className={styles.actionsContainer}>
          <Button
            as="a"
            kind="plain"
            label={props.infoLinkText}
            size="xs"
            onClick={() => {
              window.open(props.infoLinkUrl, "_blank", "noopener, noreferrer");
            }}
          />
          <Button
            label={l10n.getString("offer-card-submit", null, "Get started")}
            type="submit"
            endIcon={"arrow-right"}
            size={s ? "s" : "xs"}
            isFullWidth={props.isFullWidthCta}
            onClick={props.onSelect}
            isLoading={props.isPending}
            disabled={props.isDisabled}
          />
        </div>
      </Card>
    </div>
  );
}

export { OfferSelect };
